import React, { useState } from "react";
import { Button, Dropdown, Space } from "antd";
import {
  BarChartOutlined,
  DownloadOutlined,
  TableOutlined,
} from "@ant-design/icons";

function escapeCsvCell(value) {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function downloadTableAsCsv(fileName, headers, rows) {
  const lines = [
    headers.map(escapeCsvCell).join(","),
    ...rows.map((row) => row.map(escapeCsvCell).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Table-first presentation with optional chart and CSV download.
 * Charts stay hidden until the user chooses Chart view.
 */
export default function AprViewToggle({
  tableContent,
  chartContent,
  csvFileName,
  csvHeaders,
  csvRows,
  defaultView = "table",
  chartLabel = "Chart",
}) {
  const [view, setView] = useState(defaultView);
  const hasChart = Boolean(chartContent);
  const canDownload = Array.isArray(csvHeaders) && Array.isArray(csvRows);

  const menuItems = [
    {
      key: "table",
      icon: <TableOutlined />,
      label: "Table view",
      onClick: () => setView("table"),
    },
    ...(hasChart
      ? [
          {
            key: "chart",
            icon: <BarChartOutlined />,
            label: `${chartLabel} view`,
            onClick: () => setView("chart"),
          },
        ]
      : []),
    ...(canDownload
      ? [
          {
            key: "download",
            icon: <DownloadOutlined />,
            label: "Download table (CSV)",
            onClick: () => downloadTableAsCsv(csvFileName || "apr-table", csvHeaders, csvRows),
          },
        ]
      : []),
  ];

  return (
    <div className="apr-view-toggle">
      <div className="apr-view-toggle__toolbar no-print" style={{ marginBottom: 12, textAlign: "right" }}>
        <Space>
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button size="small">
              {view === "chart" ? <BarChartOutlined /> : <TableOutlined />} View options
            </Button>
          </Dropdown>
        </Space>
      </div>
      {view === "chart" && hasChart ? chartContent : tableContent}
    </div>
  );
}

export function isBlankDimension(value) {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  return !normalized || normalized === "NA" || normalized === "N/A" || normalized === "ANY" || normalized === "UNKNOWN" || normalized === "NONE";
}

export function formatDimensionLabel(value) {
  if (isBlankDimension(value)) return "";
  return String(value).trim();
}

export function stripDuplicateChapterPrefix(html) {
  if (!html) return html;
  return String(html)
    .replace(
      /^(?:\s|<p[^>]*>|<br\s*\/?>|&nbsp;)*((?:CHAPTER|Chapter)\s+(?:ONE|TWO|THREE|1|2|3)(?:\s*\(\d+\))?[:.\s-]*)+/i,
      "",
    )
    .replace(
      /^(?:\s|<p[^>]*>|<br\s*\/?>|&nbsp;)*((?:GENERAL\s+INFORMATION|General\s+Information)[:.\s-]*)+/i,
      "",
    );
}
